import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Home, Search, Ticket, User, Plus, Plane, MapPin, Clock, ArrowRight, ChevronRight } from "lucide-react";
import PanelShell from "../../components/PanelShell";
import StatusBadge from "../../components/StatusBadge";
import { useAuth } from "@/context/AuthContext";
import { clientApi } from "@/lib/clientApi";

const navItems = [
  { to: "/cliente", label: "Inicio", icon: Home, end: true },
  { to: "/cliente/buscar", label: "Buscar", icon: Search },
  { to: "/cliente/historial", label: "Reservas", icon: Ticket },
  { to: "/cliente/perfil", label: "Perfil", icon: User },
];

const bottomNav = [
  { to: "/cliente", label: "Inicio", icon: Home, end: true },
  { to: "/cliente/buscar", label: "Buscar", icon: Search },
  { to: "/cliente/reservar", label: "Reservar", icon: Plus },
  { to: "/cliente/historial", label: "Viajes", icon: Ticket },
  { to: "/cliente/perfil", label: "Perfil", icon: User },
];

const STATUS_LABELS = { pendiente_pago: "Pendiente", confirmado: "Confirmado", completado: "Completado", cancelado: "Cancelado" };

const greeting = () => {
  const h = new Date().getHours();
  if (h < 12) return "Buenos días";
  if (h < 19) return "Buenas tardes";
  return "Buenas noches";
};

const ClientDashboard = () => {
  const { user } = useAuth();
  const [reservations, setReservations] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [nextTripRoute, setNextTripRoute] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [reservationsRes, routesRes] = await Promise.all([clientApi.listReservations(), clientApi.listRoutes()]);
        setReservations(reservationsRes.data);
        setRoutes(routesRes.data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const nextTrip = useMemo(() => {
    const now = new Date();
    return reservations
      .filter((r) => (r.status === "pendiente_pago" || r.status === "confirmado") && new Date(r.scheduled_at) >= now)
      .sort((a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at))[0] || null;
  }, [reservations]);

  useEffect(() => {
    if (!nextTrip) {
      setNextTripRoute(null);
      return;
    }
    let cancelled = false;
    clientApi
      .getRoute(nextTrip.route_id)
      .then(({ data }) => !cancelled && setNextTripRoute(data))
      .catch(() => !cancelled && setNextTripRoute(null));
    return () => {
      cancelled = true;
    };
  }, [nextTrip]);

  const upcoming = reservations.filter((r) => r.status === "pendiente_pago" || r.status === "confirmado");
  const history = reservations.filter((r) => r.status === "completado" || r.status === "cancelado");

  return (
    <PanelShell
      role="Cliente"
      navItems={navItems}
      bottomNavItems={bottomNav}
      userName={user?.full_name}
      userMeta={user?.email}
      testId="client-dashboard"
    >
      <div className="max-w-6xl mx-auto px-4 lg:px-8 py-8 lg:py-12">
        {/* Greeting */}
        <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
          <div>
            <p className="text-[10px] uppercase tracking-brand text-[#A0A0A5] font-display">{greeting()}</p>
            <h1 className="font-display font-bold text-3xl lg:text-4xl uppercase tracking-tight mt-1">
              {user?.full_name?.split(" ")[0] || "Bienvenido"}, <span className="text-[#C9A227]">tu chofer te espera</span>
            </h1>
          </div>
          <Link
            to="/cliente/reservar"
            className="inline-flex items-center gap-2 px-5 py-3 bg-[#C9A227] hover:bg-[#D4AF37] text-[#0D0E10] font-display font-bold uppercase tracking-wider text-xs rounded-lg"
            data-testid="new-booking-btn"
          >
            <Plus size={14} /> Nueva reserva
          </Link>
        </div>

        {/* Featured cards */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Next trip */}
          <div className="lg:col-span-2 relative overflow-hidden bg-gradient-to-br from-[#1C1D20] via-[#161719] to-[#0D0E10] border border-[#C9A227]/40 rounded-xl p-8 min-h-[240px] flex flex-col justify-center" data-testid="next-trip-card">
            <div className="absolute -right-10 -top-10 w-64 h-64 rounded-full bg-[#C9A227]/10 blur-3xl" />
            {!loading && !nextTrip && (
              <div className="relative text-center">
                <p className="text-sm text-[#A0A0A5] font-body">No tienes viajes próximos agendados.</p>
                <Link to="/cliente/buscar" className="inline-block mt-4 text-xs text-[#C9A227] uppercase tracking-wider font-display">
                  Buscar una ruta →
                </Link>
              </div>
            )}
            {nextTrip && (
              <div className="relative">
                <div className="flex items-center justify-between mb-6">
                  <p className="text-[10px] uppercase tracking-brand text-[#C9A227] font-display flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#C9A227] pulse-gold" /> Próximo viaje
                  </p>
                  <StatusBadge status={STATUS_LABELS[nextTrip.status]} />
                </div>

                {(nextTrip.flight_airline || nextTrip.flight_number) && (
                  <div className="flex items-center gap-3 mb-6">
                    <Plane size={16} className="text-[#C9A227]" />
                    <p className="text-xs uppercase tracking-wider text-[#A0A0A5] font-display">
                      {[nextTrip.flight_airline, nextTrip.flight_number].filter(Boolean).join(" · ")}
                    </p>
                  </div>
                )}

                <div className="flex flex-col gap-3 mb-8">
                  {nextTripRoute ? (
                    <>
                      <div className="flex items-center gap-3">
                        <div className="w-2 h-2 rounded-full border border-[#C9A227]" />
                        <p className="font-display font-semibold text-lg">
                          {nextTripRoute.direction === "aeropuerto_ciudad" ? nextTripRoute.airport_name : nextTripRoute.city}
                        </p>
                      </div>
                      <div className="pl-1"><span className="block w-px h-4 bg-[#2E2F33]" /></div>
                      <div className="flex items-center gap-3">
                        <div className="w-2 h-2 rounded-full bg-[#C9A227]" />
                        <p className="font-display font-semibold text-lg text-[#F5F5F5]">
                          {nextTripRoute.direction === "aeropuerto_ciudad" ? nextTripRoute.city : nextTripRoute.airport_name}
                        </p>
                      </div>
                    </>
                  ) : (
                    <div className="flex items-center gap-3">
                      <MapPin size={14} className="text-[#C9A227]" />
                      <p className="text-sm text-[#F5F5F5] font-body">{nextTrip.passenger_point_address}</p>
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 pt-6 border-t border-[#2E2F33]">
                  <Info label="Fecha" value={new Date(nextTrip.scheduled_at).toLocaleDateString("es-PE")} />
                  <Info label="Hora" value={new Date(nextTrip.scheduled_at).toLocaleTimeString("es-PE", { hour: "2-digit", minute: "2-digit" })} />
                  <Info label="Modalidad" value={nextTrip.modality === "compartido" ? "Compartido" : "Privado"} />
                  <Info label="Vehículo" value={nextTripRoute?.vehicle ? `${nextTripRoute.vehicle.brand} ${nextTripRoute.vehicle.model}` : "—"} />
                </div>

                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mt-8">
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-[#A0A0A5]">Total</p>
                    <p className="font-display font-bold text-2xl text-[#C9A227]">S/ {nextTrip.price_total}</p>
                  </div>
                  <Link
                    to={`/cliente/ticket?reservationId=${nextTrip.id}`}
                    className="px-5 py-2.5 border border-[#C9A227] text-[#C9A227] hover:bg-[#C9A227]/10 uppercase tracking-wider font-display font-semibold text-xs rounded-lg"
                    data-testid="view-ticket-btn"
                  >
                    Ver ticket
                  </Link>
                </div>
              </div>
            )}
          </div>

          {/* Quick action */}
          <Link to="/cliente/reservar" className="group relative overflow-hidden bg-[#C9A227] rounded-xl p-8 flex flex-col justify-between min-h-[240px]" data-testid="quick-book-card">
            <div>
              <p className="text-[10px] uppercase tracking-brand text-[#0D0E10]/80 font-display">Rápido</p>
              <h3 className="font-display font-bold text-2xl uppercase tracking-tight mt-2 text-[#0D0E10]">Reservar traslado</h3>
              <p className="text-xs text-[#0D0E10]/70 font-body mt-3 max-w-[220px]">
                Aeropuerto ↔ Ciudad. Confirmación en menos de 2 minutos.
              </p>
            </div>
            <div className="flex items-center justify-end">
              <div className="w-10 h-10 rounded-full bg-[#0D0E10] flex items-center justify-center group-hover:translate-x-1 transition-transform">
                <ArrowRight size={16} className="text-[#C9A227]" />
              </div>
            </div>
          </Link>
        </div>

        {/* Available routes */}
        <div className="mt-10">
          <div className="flex items-end justify-between mb-4">
            <div>
              <p className="text-[10px] uppercase tracking-brand text-[#C9A227] font-display">Catálogo</p>
              <h2 className="font-display font-bold text-xl uppercase tracking-wide mt-1">Rutas disponibles</h2>
            </div>
            <Link to="/cliente/buscar" className="text-xs text-[#C9A227] uppercase tracking-wider font-display flex items-center gap-1" data-testid="view-all-routes">
              Ver todas <ChevronRight size={12} />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            {routes.slice(0, 4).map((r) => (
              <Link key={r.id} to={`/cliente/reservar?routeId=${r.id}`} className="bg-[#1C1D20] border border-[#2E2F33] hover:border-[#C9A227]/50 rounded-xl p-5 transition-all" data-testid={`quick-route-${r.id}`}>
                <p className="text-xs text-[#F5F5F5] font-body truncate">{r.airport_name}</p>
                <p className="text-xs text-[#A0A0A5] font-body truncate">→ {r.city}</p>
                <div className="mt-4 flex items-center justify-between">
                  <span className="text-[10px] text-[#A0A0A5] flex items-center gap-1"><Clock size={10} /> {r.estimated_duration_minutes} min</span>
                  <span className="text-sm font-display font-bold text-[#C9A227]">
                    S/ {r.private_enabled ? r.private_price : r.price_per_seat}
                  </span>
                </div>
              </Link>
            ))}
            {!loading && routes.length === 0 && (
              <p className="text-xs text-[#A0A0A5] font-body col-span-full">No hay rutas disponibles todavía.</p>
            )}
          </div>
        </div>

        {/* Upcoming & history */}
        <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-6">
          <ReservationList title="Próximas reservas" items={upcoming} testId="upcoming-list" />
          <ReservationList title="Historial" items={history} testId="history-list" />
        </div>
      </div>
    </PanelShell>
  );
};

const Info = ({ label, value }) => (
  <div>
    <p className="text-[10px] uppercase tracking-wider text-[#A0A0A5] font-display mb-1">{label}</p>
    <p className="text-sm font-body text-[#F5F5F5]">{value}</p>
  </div>
);

const ReservationList = ({ title, items, testId }) => (
  <div className="bg-[#1C1D20] border border-[#2E2F33] rounded-xl p-6" data-testid={testId}>
    <div className="flex items-center justify-between mb-5">
      <h3 className="font-display font-bold text-sm uppercase tracking-wider">{title}</h3>
      <span className="text-[10px] text-[#A0A0A5]">{items.length} items</span>
    </div>
    {items.length === 0 ? (
      <p className="text-xs text-[#A0A0A5] font-body">Nada por acá todavía.</p>
    ) : (
      <ul className="divide-y divide-[#2E2F33]">
        {items.slice(0, 4).map((r) => (
          <li key={r.id} className="py-3 flex items-center justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-[10px] font-mono text-[#A0A0A5]">{r.id.slice(0, 8).toUpperCase()}</span>
                <StatusBadge status={STATUS_LABELS[r.status]} />
              </div>
              <p className="text-sm font-body text-[#F5F5F5] truncate">{r.passenger_point_address}</p>
              <p className="text-[10px] text-[#A0A0A5] mt-0.5">{new Date(r.scheduled_at).toLocaleString("es-PE")}</p>
            </div>
            <div className="text-right">
              <p className="font-display font-bold text-[#C9A227] text-sm">S/ {r.price_total}</p>
            </div>
          </li>
        ))}
      </ul>
    )}
  </div>
);

export default ClientDashboard;
